import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope } from '@/types/api';
import type { PaymentMethod } from '@/types/payment';

export async function getPaymentMethods(): Promise<PaymentMethod[]> {
  try {
    const res = await http.get<ApiEnvelope<PaymentMethod[]>>('/payment-method');
    const data = unwrap(res.data);
    const rows = Array.isArray(data) ? data : [];
    // Honor the back-office "Show in POS" flag. The backend already hides
    // showInPos:false rows from this authenticated list by default, but we
    // filter here too so a hidden tender never reaches the chooser OR the
    // checkout quote-method pick even if that default ever changes. Absent /
    // true stays visible; only an explicit false is dropped.
    //
    // Credit-card tenders ("Kartu Kredit BCA") are dropped too: they open an
    // online card page the customer fills in on their own device, which a
    // cashier cannot hand over. They also carry their own promo, so one left in
    // the list could become the checkout quote method and show a price the
    // chosen tender would not charge.
    return rows
      .filter((m) => m.showInPos !== false && !isCreditCardTender(m))
      .map(normalizeOfflineTender);
  } catch (error) {
    throw toApiError(error);
  }
}

/** A credit-card tender — an online card page, possibly limited to one bank. */
export function isCreditCardTender(m: Pick<PaymentMethod, 'paymentMethod'>): boolean {
  return String(m.paymentMethod ?? '').trim().toLowerCase() === 'credit-card';
}

/**
 * Give an OFFLINE tender the `cash` gateway every branch of this app keys on.
 *
 * The backend spells "no gateway" as the empty string (`payment_gateway` is NOT
 * NULL), and that is how the seeded "Cash" row is stored; rows carried over from
 * the Mongo era say `cash` / `CASH` instead. Only `cash` was recognised here, so
 * the seeded row fell through to the QRIS branch: the sale settled server-side
 * as cash, and the cashier was shown "Kode QRIS tidak tersedia" for it.
 *
 * Sending `cash` back is what the sale route expects for this tender anyway —
 * it is the only offline value the validator accepts. The group-booking
 * singleton is the other gateway-less row, and it never gets here: it is
 * `showInPos: false`, filtered out above.
 */
function normalizeOfflineTender(m: PaymentMethod): PaymentMethod {
  const gateway = String(m.paymentGateway ?? '').trim().toLowerCase();
  const method = String(m.paymentMethod ?? '').trim().toLowerCase();
  if (gateway === 'cash' || method === 'cash' || (gateway === '' && method === '')) {
    return { ...m, paymentGateway: 'cash' };
  }
  return m;
}
